// React core and hooks
import React, { useRef, useEffect, useState } from 'react'
import { PropTypes } from 'prop-types'
import ReactMarkdown from 'react-markdown'
// Firebase Imports
import { db, auth } from '../firebase'
import { collection, query, where, onSnapshot, doc, getDocs } from 'firebase/firestore'
// MUI Components and Icons
import { Container, Box, List, TextField, InputAdornment, IconButton, useTheme, useMediaQuery, Grid, Typography, Alert, Button, AlertTitle } from '@mui/material'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
// Custom components
import ChatMessage from '../components/ChatMessage'
import DialogSlide from '../components/DialogSlide'
import BasicModal from '../components/modals/BasicModal'
import QuestStepper from '../components/QuestStepper'
import QuestMessage from '../components/QuestMessage'
import AlertDialog from '../components/AlertDialog'

const WELCOME_MESSAGE = 'Welcome to the Peacegame. In this game you will be confronted with some scenarios. For each scenario you can input your strategy how to tackle it. The AI will evaluate your input and give you an assesment of your actions.'
const URL = 'https://task-somalia-688160029673.europe-west10.run.app/quest'
function ChatPage ({ gameRef, playerName, setIsPlaying }) {
  // Themeing
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTabletorMobile = useMediaQuery(theme.breakpoints.down('md'))
  const pageStyle = {
    content: { height: isMobile ? 'calc(100vh - 56px)' : 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
    messagesContainer: { maxWidth: '820px', m: 'auto', height: '90%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', flexGrow: 1, flexShrink: 1, overflow: 'auto' },
    sidebarContainer: { marginTop: '2px', backgroundColor: '#F7F9F9', display: isTabletorMobile ? 'none' : 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', padding: '20px' },
    messageList: { m: '0px 3px 0px 3px', overflow: 'auto' },
    inputContainer: { maxWidth: '820px', width: '100%', m: 'auto', display: 'flex', alignItems: 'center', flexGrow: 0, flexShrink: 0, mt: 1 },
    textField: { mr: '5px', mb: '5px' }
  }
  // State
  const gameId = gameRef
  const [gameTurn, setGameTurn] = useState(0)
  const [messages, setMessages] = useState([])
  const [newMessage, setNewMessage] = useState('')
  const [messageHistory, setMessageHistory] = useState([])
  const [quests, setQuests] = useState([])
  const [summaries, setSummaries] = useState([])
  const [currentQuest, setCurrentQuest] = useState({})
  const [isAlertOpen, setIsAlertOpen] = useState(false)
  const [isTyping, setIsTyping] = useState(false)
  const [isFailed, setIsFailed] = useState(false)
  const [isLocked, setIsLocked] = useState(false)
  const [blockSend, setBlockSend] = useState(true)

  const messagesEndRef = useRef()

  useEffect(() => {
    const fetchQuests = async () => {
      const questsDocs = []
      const questSnap = await getDocs(collection(db, 'quests'))
      questSnap.forEach((quest) =>
        questsDocs.push(
          {
            id: quest.id,
            ...quest.data(),
            type: 'quest'
          }
        ))
      questsDocs.sort((a, b) => a.turn - b.turn)
      setQuests(questsDocs)
    }

    fetchQuests()
    // Listening to messages collection
    const messagesQuery = query(
      collection(db, 'messages'),
      where('userId', '==', auth.currentUser.uid),
      where('gameId', '==', gameId)
    )
    const messagesUnsubscribe = onSnapshot(messagesQuery, (querySnapshot) => {
      const messagesDocs = []
      querySnapshot.forEach((doc) => {
        messagesDocs.push({
          ...doc.data(), type: 'msg'
        })
      })
      messagesDocs.sort((a, b) => a.createdAt - b.createdAt)
      setMessages(messagesDocs)
      if (messagesDocs.slice(-1)[0]?.role === 'assistant') {
        setIsTyping(false)
      }
    })

    const summariesQuery = query(
      collection(db, 'horizontal'),
      where('gameId', '==', gameId))

    const summariesUnsubscribe = onSnapshot(summariesQuery,
      (querySnapshot) => {
        const summariesDocs = []
        querySnapshot.forEach((doc) => {
          summariesDocs.push({
            ...doc.data(), type: 'sum'
          })
          setSummaries(summariesDocs)
        }
        )
      })

    // Listening to game state updates
    const gameRef = doc(db, 'games', gameId)
    const gameUnsubcribe = onSnapshot(gameRef, (doc) => {
      if (doc.exists()) {
        if (doc.data().isLocked === true) {
          setIsLocked(doc.data().isLocked)
        }
        if (doc.data().active === false) {
          setIsPlaying(false)
        }
        setGameTurn(doc.data().turn)
      } else {
        // Handle the case where the document does not exist
        console.error('No such document!')
      }
    }, (err) => {
      console.error('Error listening to document:', err)
    })
    // Cleanup function to unsubscribe from both collections

    return () => {
      summariesUnsubscribe()
      messagesUnsubscribe()
      gameUnsubcribe()
    }
  }, [])

  function customSort (array) {
    array.sort((a, b) => {
      if (a.turn !== b.turn) {
        return a.turn - b.turn // Sort by turn in ascending order
      }
      // If turns are the same, sort by type
      const typeOrder = { quest: 1, msg: 2, sum: 3 }
      return typeOrder[a.type] - typeOrder[b.type]
    })
    return array
  }

  useEffect(() => {
    setMessageHistory(customSort([...messages, ...quests.filter((quest) => quest.turn <= gameTurn), ...summaries]))
    setBlockSend(messages.some(item => item.turn === gameTurn) || gameTurn === 0)
    setCurrentQuest(quests.find(quest => quest.turn === gameTurn))
  }, [messages, quests, gameTurn, summaries])

  // scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'instant' })
  }, [messageHistory])

  async function processMessageToAPI (url, chatMessage, username, gameId, userId, turn) {
    try {
      setIsTyping(true)
      const apiRequestBody = {
        content: chatMessage,
        name: username,
        gameId,
        userId,
        quest_id: currentQuest.id,
        turn
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(apiRequestBody)
      })
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      // assumes i get something back
      const data = await response.json()

      return data
    } catch (error) {
      console.error('Error in processMessageToAPI: ', error)
      setIsFailed(true)
      setIsTyping(false)
    }
  }

  const resendRequest = async () => {
    const lastMessage = messageHistory.slice(-1)
    setIsFailed(false)
    await processMessageToAPI(URL, lastMessage.content, lastMessage.playerName, gameId, auth.currentUser.uid, gameTurn)
  }

  const handleSendMessage = async (e) => {
    e?.preventDefault()
    setIsFailed(false)
    if (!newMessage.trim()) return

    try {
      const messageObject = {
        content: newMessage.trim(),
        userId: auth.currentUser.uid,
        name: playerName,
        role: 'user',
        type: 'msg',
        gameId,
        turn: gameTurn
      }

      setNewMessage('')
      // add message to local state
      setMessageHistory(prevMessages => [...prevMessages, messageObject])
      // send message to API
      await processMessageToAPI(
        URL,
        newMessage.trim(),
        playerName,
        gameId,
        auth.currentUser.uid,
        gameTurn)

      setBlockSend(true)
      console.log('Message sent successfully')
    } catch (error) {
      console.error('Error sending message:', error)
      alert('We experiencing high traffic at the moment. Please try again in a few seconds')
    }
  }

  return (
    <Grid container>
      <Grid item md={4} sx={ pageStyle.sidebarContainer}>
          <QuestStepper activeStepNumber={gameTurn} quests={[{ turn: 0, theme: 'Welcome to Peacegame', task: 'Please wait until the game starts.' }, ...quests, { turn: quests.length + 1, theme: 'Game has ended', task: 'Thank you for your participation' }]} ></QuestStepper>
      </Grid>
      <Grid item xs={12} md={8} >
          {isTabletorMobile &&
          <DialogSlide>
            <QuestStepper activeStepNumber={gameTurn} quests={[{ turn: 0, theme: 'Welcome to Peacegame', task: 'Please wait until the game starts.' }, ...quests, { turn: quests.length + 1, theme: 'Game has ended', task: 'Thank you for your participation' }]} ></QuestStepper>
          </DialogSlide>}
      <Container sx={ pageStyle.content }>
          <Box sx={ pageStyle.messagesContainer }>
              <List sx={pageStyle.messageList}>
              <ChatMessage key={-1} uniqueId={-1} content={WELCOME_MESSAGE} role={'assistent'} playerName={playerName} ></ChatMessage>
                {messageHistory.map((message, index) => {
                  if (message.type === 'msg' && message.turn === gameTurn) {
                    return <ChatMessage key={index} uniqueId={index} content={message.content} role={message.role}/>
                  } else if (message.type === 'quest' && message.turn === gameTurn) {
                    return <QuestMessage key={index} uniqueId={index} background={message.background} task={message.task} label={message.theme} turn={message.turn} />
                  } else if (message.type === 'sum' && message.turn === gameTurn) {
                    return (
                    <>
                    <Alert variant='outlined' icon={false} severity='info' sx={{ mb: '5px' }}>
                    <AlertTitle> Horizontal Evaluation </AlertTitle>
                    <Typography component="div">
                      <ReactMarkdown>
                        {message.content}
                      </ReactMarkdown>
                    </Typography>
                    </Alert>
                    </>
                    )
                  } else { return <></> }
                })}
                {isTyping && <ChatMessage key={-2} uniqueId={-2} content={'AI is typing...'} role={'assistent'} playerName={playerName} ></ChatMessage>}
                {isFailed && <><Alert severity="warning" action={<Button color="inherit" onClick={resendRequest} size="small">RESEND</Button>}>Something went wrong. Please try again in a few seconds.</Alert> </>}
                <div ref={ messagesEndRef }></div>
              </List>
          </Box>
          <Box sx={ pageStyle.inputContainer }>
              <TextField
              fullWidth
              variant="outlined"
              label={blockSend ? 'Waiting for the next quest' : 'Submit your action'}
              value={newMessage}
              disabled={isTyping || blockSend}
              onChange={(e) => { setNewMessage(e.target.value) }}
              multiline
              maxRows={3}
              InputProps={{
                endAdornment: (
                    <InputAdornment position="end">
                      <IconButton edge="end" disabled={blockSend || isTyping } onClick={() => { setIsAlertOpen(true) }}>
                        <ArrowUpwardIcon />
                      </IconButton>
                    </InputAdornment>
                )
              }}
              sx={pageStyle.textField}
              />
          </Box>
      </Container>
      </Grid>
    <BasicModal isOpen={isLocked} title={'Game Paused'} handleClose={() => { setIsLocked(false) }} >
      <Box sx={{ mb: '10px' }}>
        <Typography variant='body' sx={{ mb: 3, whiteSpace: 'pre-wrap' }}>
            Hier könnte ihre Werbung stehen.
        </Typography>
      </Box>
    </BasicModal>
    <AlertDialog isOpen={isAlertOpen} handleClose={() => { setIsAlertOpen(false) }} dialogTitle={'Submit your action?'} dialogContent={'Are you sure you want to submit your action? An action can only be submitted once per quest.'} handleAgree={ () => { handleSendMessage() } } ></AlertDialog>
    </Grid>
  )
}

ChatPage.propTypes = {
  gameRef: PropTypes.string.isRequired,
  playerName: PropTypes.string.isRequired,
  setIsPlaying: PropTypes.func.isRequired
}

export default ChatPage
